import React, { useState, useEffect } from 'react'
import LeadActivityModal from './LeadActivityModal'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  GridList,
  GridListTile,
  CircularProgress,
  Tab,
  Tabs
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  SwapHoriz as ConvertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  History as HistoryIcon,
  Sms as SmsIcon
} from '@material-ui/icons'
import moment from 'moment'
import axios from 'axios'

const LeadDetailModal = ({ open, lead, onClose, onEdit, onConvert }) => {
  const [fullLead, setFullLead] = useState(lead)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [selectedImage, setSelectedImage] = useState(null)
  const [activities, setActivities] = useState([])
  const [activityModalOpen, setActivityModalOpen] = useState(false)
  const [loadingActivities, setLoadingActivities] = useState(false)

  useEffect(() => {
    if (open && lead?._id) {
      fetchFullLead()
      if (activeTab === 2) {
        fetchActivities()
      }
    }
  }, [open, lead])

  useEffect(() => {
    if (activeTab === 2 && lead?._id) {
      fetchActivities()
    }
  }, [activeTab, lead])

  const fetchFullLead = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`/api/company/leads/${lead._id}`)
      if (response.data.success) {
        setFullLead(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching lead details:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchActivities = async () => {
    if (!lead?._id) return
    
    setLoadingActivities(true)
    try {
      const response = await axios.get(`/api/company/leads/${lead._id}/activities`)
      if (response.data.success) {
        setActivities(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching activities:', error)
    } finally {
      setLoadingActivities(false)
    }
  }

  const handleActivityAdded = () => {
    fetchActivities()
    fetchFullLead() // Refresh lead to get updated status and last activity
  }

  const getStatusColor = (status) => {
    const colors = {
      new: 'primary',
      contacted: 'info',
      qualified: 'warning',
      converted: 'success',
      lost: 'error'
    }
    return colors[status] || 'default'
  }

  const getStatusIcon = (status) => {
    if (status === 'converted') return <CheckCircleIcon fontSize="small" />
    if (status === 'lost') return <CancelIcon fontSize="small" />
    return null
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const sitePhotos = fullLead?.photos || []
  const ocrPhotoEntries = fullLead?.ocrPhotos ? Object.entries(fullLead.ocrPhotos)
    .filter(([key, photo]) => photo?.uri)
    .map(([key, photo]) => ({
      ...photo,
      id: `ocr_${key}`,
      label: key === 'company' ? 'Company Name (OCR)' :
             key === 'address' ? 'Address (OCR)' :
             key === 'contact' ? 'Contact Info (OCR)' :
             key === 'business_card' ? 'Business Card' :
             `OCR: ${key}`,
      isOCR: true,
      ocrType: key,
      hasText: photo.ocrSuccess && photo.extractedText
    })) : []
  
  const allPhotos = [...sitePhotos, ...ocrPhotoEntries]

  if (!fullLead) return null

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          style: { minHeight: '60vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">{fullLead.companyName}</Typography>
              <Chip
                label={fullLead.status}
                color={getStatusColor(fullLead.status)}
                size="small"
                icon={getStatusIcon(fullLead.status)}
              />
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                  <Tab label="Details" />
                  <Tab label="Photos" disabled={allPhotos.length === 0} />
                  <Tab label="Activities" />
                </Tabs>
                {activeTab === 2 && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setActivityModalOpen(true)}
                    variant="contained"
                    color="primary"
                    size="small"
                  >
                    Add Activity
                  </Button>
                )}
              </Box>

              {activeTab === 0 && (
                <Grid container spacing={3}>
                  {/* Company Information */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Company Information
                    </Typography>
                    <Divider />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {fullLead.address}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {fullLead.coordinates && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="textSecondary">
                        Coordinates
                      </Typography>
                      <Typography variant="body2">
                        {fullLead.coordinates.latitude}, {fullLead.coordinates.longitude}
                      </Typography>
                    </Grid>
                  )}

                  {/* Contact Information */}
                  {(fullLead.contactName || fullLead.contactEmail || fullLead.contactPhone) && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Contact Information
                        </Typography>
                        <Divider />
                      </Grid>

                      {fullLead.contactName && (
                        <Grid item xs={12} md={4}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon color="action" />
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                Contact Name
                              </Typography>
                              <Typography variant="body1">
                                {fullLead.contactName}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {fullLead.contactEmail && (
                        <Grid item xs={12} md={4}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmailIcon color="action" />
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                Email
                              </Typography>
                              <Typography 
                                variant="body1" 
                                component="a" 
                                href={`mailto:${fullLead.contactEmail}`}
                                style={{ color: 'inherit', textDecoration: 'none' }}
                              >
                                {fullLead.contactEmail}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {fullLead.contactPhone && (
                        <Grid item xs={12} md={4}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PhoneIcon color="action" />
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                Phone
                              </Typography>
                              <Typography 
                                variant="body1"
                                component="a" 
                                href={`tel:${fullLead.contactPhone}`}
                                style={{ color: 'inherit', textDecoration: 'none' }}
                              >
                                {fullLead.contactPhone}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                    </>
                  )}

                  {/* Interaction Details */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Interaction Details
                    </Typography>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box display="flex" gap={2}>
                      {fullLead.leftFlyer && (
                        <Chip label="Left Flyer" color="primary" variant="outlined" />
                      )}
                      {fullLead.spokeWithSomeone && (
                        <Chip label="Spoke with Someone" color="primary" variant="outlined" />
                      )}
                      {!fullLead.leftFlyer && !fullLead.spokeWithSomeone && (
                        <Typography variant="body2" color="textSecondary">
                          No interaction recorded
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Notes */}
                  {fullLead.notes && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Notes
                        </Typography>
                        <Divider />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                          {fullLead.notes}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {/* Follow-up Information */}
                  {(fullLead.followUpDate || fullLead.followUpNotes) && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Follow-up Information
                        </Typography>
                        <Divider />
                      </Grid>
                      
                      {fullLead.followUpDate && (
                        <Grid item xs={12} md={6}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarIcon color="action" />
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                Follow-up Date
                              </Typography>
                              <Typography variant="body1">
                                {moment(fullLead.followUpDate).format('MMMM DD, YYYY')}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                      
                      {fullLead.followUpNotes && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="textSecondary">
                            Follow-up Notes
                          </Typography>
                          <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                            {fullLead.followUpNotes}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}

                  {/* Metadata */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Lead Information
                    </Typography>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="textSecondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {moment(fullLead.createdAt).format('MMMM DD, YYYY h:mm A')}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="textSecondary">
                      Created By
                    </Typography>
                    <Typography variant="body2">
                      {fullLead.createdBy ? 
                        `${fullLead.createdBy.firstName} ${fullLead.createdBy.lastName}` : 
                        'Unknown'
                      }
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="textSecondary">
                      Source
                    </Typography>
                    <Typography variant="body2">
                      {fullLead.source || 'Mobile App'}
                    </Typography>
                  </Grid>

                  {fullLead.crew && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary">
                        Crew
                      </Typography>
                      <Typography variant="body2">
                        {fullLead.crew.name}
                      </Typography>
                    </Grid>
                  )}

                  {fullLead.convertedToCustomer && (
                    <>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="textSecondary">
                          Converted To
                        </Typography>
                        <Typography variant="body2">
                          {fullLead.convertedToCustomer.displayName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="textSecondary">
                          Converted Date
                        </Typography>
                        <Typography variant="body2">
                          {moment(fullLead.convertedDate).format('MMMM DD, YYYY')}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}

              {activeTab === 1 && (
                <Box>
                  {allPhotos.length > 0 ? (
                    <Box>
                      {/* OCR Photos Section */}
                      {ocrPhotoEntries.length > 0 && (
                        <Box marginBottom={3}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            OCR Photos (Business Cards, Documents)
                          </Typography>
                          <Divider style={{ marginBottom: 16 }} />
                          <GridList cols={3} spacing={8}>
                            {ocrPhotoEntries.map((photo) => (
                              <GridListTile 
                                key={photo.id} 
                                style={{ cursor: 'pointer' }}
                                onClick={() => setSelectedImage(photo)}
                              >
                                <img
                                  src={photo.url || photo.uri}
                                  alt={photo.label}
                                  loading="lazy"
                                  style={{ height: 200, objectFit: 'cover' }}
                                />
                                <Box
                                  style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    backgroundColor: photo.hasText ? 'rgba(76, 175, 80, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                                    color: 'white',
                                    padding: 4
                                  }}
                                >
                                  <Typography variant="caption">{photo.label}</Typography>
                                  {photo.hasText && (
                                    <Typography variant="caption" display="block">
                                      ✓ Text extracted
                                    </Typography>
                                  )}
                                </Box>
                              </GridListTile>
                            ))}
                          </GridList>
                        </Box>
                      )}
                      
                      {/* Site Photos Section */}
                      {sitePhotos.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Site Photos
                          </Typography>
                          <Divider style={{ marginBottom: 16 }} />
                          <GridList cols={3} spacing={8}>
                            {sitePhotos.map((photo, index) => (
                              <GridListTile 
                                key={photo.id || index} 
                                style={{ cursor: 'pointer' }}
                                onClick={() => setSelectedImage(photo)}
                              >
                                <img
                                  src={photo.url || photo.uri}
                                  alt={`Site photo ${index + 1}`}
                                  loading="lazy"
                                  style={{ height: 200, objectFit: 'cover' }}
                                />
                              </GridListTile>
                            ))}
                          </GridList>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" p={4}>
                      <ImageIcon style={{ fontSize: 48, color: '#ccc' }} />
                      <Typography color="textSecondary">No photos available</Typography>
                    </Box>
                  )}
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  {loadingActivities ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress />
                    </Box>
                  ) : activities.length > 0 ? (
                    <Box>
                      {activities.map((activity, index) => (
                        <Box key={index} marginBottom={2} padding={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box display="flex" gap={1}>
                              {activity.type === 'phone_call' && <PhoneIcon color="primary" />}
                              {activity.type === 'email' && <EmailIcon color="primary" />}
                              {activity.type === 'sms' && <SmsIcon color="primary" />}
                              {activity.type === 'visit' && <LocationIcon color="primary" />}
                              {activity.type === 'note' && <DescriptionIcon color="primary" />}
                              <Box>
                                <Typography variant="subtitle2">
                                  {activity.type === 'phone_call' && 'Phone Call'}
                                  {activity.type === 'email' && 'Email'}
                                  {activity.type === 'sms' && 'SMS/Text'}
                                  {activity.type === 'visit' && 'Site Visit'}
                                  {activity.type === 'note' && 'Note'}
                                  {activity.outcome && ` - ${activity.outcome}`}
                                </Typography>
                                {activity.contactPerson && (
                                  <Typography variant="caption" color="textSecondary">
                                    Contact: {activity.contactPerson}
                                  </Typography>
                                )}
                                {activity.duration && (
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    Duration: {activity.duration} minutes
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box textAlign="right">
                              <Typography variant="caption" color="textSecondary">
                                {moment(activity.createdAt).format('MMM DD, YYYY h:mm A')}
                              </Typography>
                              {activity.createdBy && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                  by {activity.createdBy.firstName} {activity.createdBy.lastName}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Typography variant="body2" style={{ marginTop: 8 }}>
                            {activity.description}
                          </Typography>
                          {activity.nextAction && (
                            <Box marginTop={1} padding={1} style={{ backgroundColor: '#fff', borderRadius: 4 }}>
                              <Typography variant="caption" color="primary">
                                Next Action: {activity.nextAction}
                                {activity.nextActionDate && ` - ${moment(activity.nextActionDate).format('MMM DD, YYYY')}`}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" p={4}>
                      <HistoryIcon style={{ fontSize: 48, color: '#ccc' }} />
                      <Typography color="textSecondary" style={{ marginTop: 16 }}>
                        No activities recorded yet
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => setActivityModalOpen(true)}
                        variant="outlined"
                        color="primary"
                        style={{ marginTop: 16 }}
                      >
                        Add First Activity
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          {fullLead?.status !== 'converted' && (
            <>
              <Button onClick={onEdit} startIcon={<EditIcon />}>
                Edit
              </Button>
              <Button onClick={onConvert} startIcon={<ConvertIcon />} color="primary">
                Convert to Customer
              </Button>
            </>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      {selectedImage && (
        <Dialog
          open={Boolean(selectedImage)}
          onClose={() => setSelectedImage(null)}
          maxWidth="lg"
        >
          <DialogContent>
            <img
              src={selectedImage.url || selectedImage.uri}
              alt={selectedImage.label || 'Lead photo'}
              style={{ width: '100%', height: 'auto' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedImage(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Activity Modal */}
      <LeadActivityModal
        open={activityModalOpen}
        lead={fullLead}
        onClose={() => setActivityModalOpen(false)}
        onSave={handleActivityAdded}
      />
    </>
  )
}

export default LeadDetailModal